import { locationUtil } from '@grafana/data';
import { t } from '@grafana/i18n';
import { getAppEvents, locationService } from '@grafana/runtime';
import { getExploreUrl } from 'app/core/utils/explore';
import { getTimeSrv } from 'app/features/dashboard/services/TimeSrv';
import { type SavedQuery } from 'app/features/explore/QueryLibrary/types';
import { updateQueryLibraryRefAction } from 'app/features/explore/state/explorePane';
import { store, dispatch } from 'app/store/store';
import { ShowConfirmModalEvent } from 'app/types/events';

const PANE_SETUP_DELAY = 200;

type OpenInExploreOptions = {
  /**
   * Link the Explore pane back to the saved query (editing mode). Defaults to true; set false to just
   * load the query to run it (e.g. from the command palette).
   */
  setQueryLibraryRef?: boolean;
};

export const onOpenInExplore = async (
  query: SavedQuery,
  onCloseDrawer?: (isSelectingQuery?: boolean, closedToEditInExplore?: boolean) => void,
  options?: OpenInExploreOptions
) => {
  if (!query.query || !query.datasourceRef) {
    console.error('Missing required query or datasource reference');
    return;
  }

  // Get current explore state
  const { exploreState, currentPaneIds } = getCurrentExploreState();

  // Check if we should show the warning modal
  const currentQueries = currentPaneIds.flatMap((id) => exploreState[id]?.queries);
  if (currentQueries.length > 1) {
    // Show modal for multiple queries
    showMultipleQueriesModal(query, onCloseDrawer, options);
  } else {
    await performDirectNavigation(query, onCloseDrawer, options);
  }
};

// Helper functions

const getCurrentExploreState = () => {
  const exploreState = store.getState().explore.panes;
  const currentPaneIds = Object.keys(exploreState).filter((id) => exploreState[id] !== undefined);
  return { exploreState, currentPaneIds };
};

const performDirectNavigation = async (
  query: SavedQuery,
  onCloseDrawer?: (isSelectingQuery?: boolean, closedToEditInExplore?: boolean) => void,
  options?: OpenInExploreOptions
) => {
  try {
    const timeRange = getTimeSrv().timeRange();
    const exploreUrl = await getExploreUrl({
      queries: [query.query],
      dsRef: query.datasourceRef,
      timeRange,
      scopedVars: undefined,
    });

    if (!exploreUrl) {
      console.error('Failed to generate explore URL');
      return;
    }

    const url = new URL(exploreUrl, window.location.origin);
    const panesParam = url.searchParams.get('panes');

    if (!panesParam) {
      console.error('No panes parameter found in explore URL');
      return;
    }

    let panes;
    try {
      panes = JSON.parse(panesParam);
    } catch (parseError) {
      console.error('Failed to parse panes parameter:', parseError);
      return;
    }

    const newPaneId = Object.keys(panes)[0];
    if (!newPaneId) {
      console.error('No pane ID found in parsed panes');
      return;
    }

    // Navigate to explore. If the current page has unsaved work, Grafana's leave-page guard
    // (DashboardPrompt's history.block) intercepts this push and asks the user to save, discard,
    // or cancel before continuing.
    locationService.push(locationUtil.stripBaseFromUrl(exploreUrl));

    // Set queryLibraryRef after navigation so Explore enters editing mode for this saved query.
    // Skipped when opening just to run the query (e.g. from the command palette).
    if (options?.setQueryLibraryRef !== false) {
      setTimeout(() => {
        try {
          dispatch(
            updateQueryLibraryRefAction({
              exploreId: newPaneId,
              queryLibraryRef: query.uid,
            })
          );
        } catch (dispatchError) {
          console.error('Failed to set query library reference:', dispatchError);
        }
      }, PANE_SETUP_DELAY);
    }

    // Close the drawer. When the guard above is showing, this leaves it displayed on its own
    // (no query-library drawer lingering underneath) whatever the user then chooses.
    onCloseDrawer?.(false, true);
  } catch (error) {
    console.error('Failed to open in explore:', error);
  }
};

const showMultipleQueriesModal = (
  query: SavedQuery,
  onCloseDrawer?: (isSelectingQuery?: boolean, closedToEditInExplore?: boolean) => void,
  options?: OpenInExploreOptions
) => {
  const modalConfig = buildModalConfig(query, onCloseDrawer, options);
  getAppEvents().publish(new ShowConfirmModalEvent(modalConfig));
};

// Helper function to build modal configuration
const buildModalConfig = (
  query: SavedQuery,
  onCloseDrawer?: (isSelectingQuery?: boolean, closedToEditInExplore?: boolean) => void,
  options?: OpenInExploreOptions
) => {
  // The command palette opens the query to run it (setQueryLibraryRef: false), not to edit it, so
  // use "Opening" there; the "Edit in Explore" flow keeps "Editing".
  const warningText =
    options?.setQueryLibraryRef === false
      ? t(
          'query-library.explore-modal.warning-open',
          'You have multiple queries in Explore. Opening this saved query will replace all existing queries. Do you want to continue?'
        )
      : t(
          'query-library.explore-modal.warning',
          'You have multiple queries in Explore. Editing this saved query will replace all existing queries. Do you want to continue?'
        );

  return {
    title: t('query-library.explore-modal.title', 'Replace existing queries?'),
    text: warningText,
    yesText: t('query-library.explore-modal.confirm-button', 'Replace queries'),
    noText: t('query-library.explore-modal.cancel-button', 'Cancel'),
    icon: 'exclamation-triangle' as const,
    onConfirm: () => performDirectNavigation(query, onCloseDrawer, options),
  };
};
